import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import { useState, useEffect, useContext } from 'react';
import React from 'react';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { Icon, Icons } from '@ge/components/icon';
import { Menu } from '@ge/components/menu';
import { Dialog } from '@ge/components/modal';
import { Tooltip } from '@ge/components/tooltip';
import { usePatchAnnotionJob } from '@ge/feat-inspections/data-hooks/use-damages';
import { Capability, EntityType } from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { elevations } from '@ge/tokens';

import HeaderCurrentSite from '../components/header-current-site';
import EntityDetailHeader, { HeaderIcon } from '../entity-details-header';
import { DetailsCurrent } from '../entity-details-shared';
import { DetailsCurrentSeparator } from '../entity-details-shared';

import DamageDetailsWeather from './damage-details-weather';

const EDIT_APPROVAL_FLAG_OPTIONS = [
  { label: 'OPEN', value: 'Open' },
  { label: 'CLOSE', value: 'Closed' },
];

const StyledEntityDetailHeader = styled(EntityDetailHeader)`
  flex-direction: column;
  margin-left: 0;
  margin-right: 0;
  .details {
    background: ${(props) => props.theme.entityDetails.headerBackground};
    padding: 10px;
  }
`;

const StyledHeaderCurrentSite = styled(HeaderCurrentSite)`
  color: ${(props) => props.theme.colors.slate5} !important;
  svg {
    fill: ${(props) => props.theme.colors.slate5} !important;
  }
`;

const TurbineIcon = styled(Icon).attrs(() => ({
  size: 35,
  icon: Icons.TURBINE,
}))`
  margin-right: 10px;
  fill: #8694a1;
`;
const AnamoliesWeatherContainer = styled.div`
  flex: 1 0;
`;
const DropDown = styled.div`
  position: relative;
  display: flex;
  background: none;
  border: none;
  gap: 5px;
  button {
    background: none;
    border: none;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    padding: 0px;
    align-self: start;
  }
`;
const StyleApprovalFlagText = styled.p`
  border-radius: 4px;
  margin: 0;
  padding: 10px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 300;
  &:hover {
    background-color: ${(props) => props.theme.select.hoverColor};
  }
  .check-icon {
    float: right;
  }
`;
const Divider = styled.div`
  border: solid 1px #1c252d;
  margin-left: 20px;
  height: 60px;
`;

const DropDownTitle = styled.div`
  color: ${(props) => props.theme.colors.grey4};
  font-size: 11px;
  font-family: 'Museo Sans';
  font-weight: 300;
  margin-left: 4px;
`;

const Anamolies = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 5px;
`;
const StyledTurbineName = styled.h2`
  font-size: 25px;
  margin-top: 5px;
`;
const StyledAddress = styled.h2`
  font-size: 12px;
`;
const TurbineModel = styled.p`
  margin: 0;
`;
const StyledDetailHeader = styled.div`
  max-width: 360px;
  display: inline-block;
`;
const StyledHeaderCurrentCustomer = styled.div`
  color: ${(props) => props.theme.colors.slate5};
  display: flex;
  align-items: center;
  padding: 0px;
  svg {
    fill: ${(props) => props.theme.colors.slate5};
  }
`;
const CustomerName = styled.h5`
  max-width: 85px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(props) => props.theme.colors.slate5};
`;
const StatusFlagText = styled.div`
  margin-top: 10px;
`;
const ButtonSection = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 10px;
`;

const CancelButton = styled(Button)`
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.deleteCaseTemplate.cancelBorderColor};
`;

const ConfirmButton = styled(Button)`
  border-radius: 4px;
  margin: 0 20px 0 10px;
`;
const StyledIcon = styled(Icon).attrs((props) => ({
  size: 12,
  color: props.theme.entityDetails.tickets.iconColor,
}))`
  margin-right: 5px;
`;

const DamageDetailsHeader = ({ damageDetails, assetSiteId, onStatusUpdate, status, isEdited }) => {
  const [customerName, setCustomerName] = useState('');
  const [regionName, setRegionName] = useState('');
  const allSites = useStoreState((state) => state.sites.sites);
  const { getAssetById } = useStoreState((store) => store.assets);
  const asset = getAssetById(damageDetails?.asset_site_id);
  const siteData = asset?.site?.id ? allSites[asset?.site?.id] : allSites[assetSiteId];
  const allRegion = useStoreState((state) => state.regions.regions);
  const regionData = siteData && allRegion ? allRegion[siteData?.region?.id] : null;
  const [showStatusFlagModal, setShowStatusFlagModal] = useState(false);
  const [selectedDamageFlagValue, setSelectedDamageFlagValue] = useState(
    EDIT_APPROVAL_FLAG_OPTIONS.find(
      (option) => option.value === damageDetails.annotationResult.data.attributes.status,
    ),
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const { mutate: patchAnnotationJob } = usePatchAnnotionJob();

  const { setIsDamageStatusUpdated, setIsDamageUpdated } = useContext(EntityDetailsContext);

  useEffect(() => {
    if (siteData) {
      setCustomerName(siteData.customer.name ? siteData.customer.name : '');
      setRegionName(regionData ? regionData.name : '');
    } else {
      setCustomerName(damageDetails.reportJsonMetadata['TURBINE MASTER DATA']?.Customer);
      setRegionName(damageDetails.reportJsonMetadata?.Header?.['Project Name']);
    }
  }, [damageDetails.reportJsonMetadata, regionData, siteData]);

  const contactAddress = `${
    damageDetails.reportJsonMetadata['TURBINE MASTER DATA']?.['Site Address'] || ''
  } ${damageDetails.reportJsonMetadata['TURBINE MASTER DATA']?.['Site State/Province'] || ''} ${
    damageDetails.reportJsonMetadata['TURBINE MASTER DATA']?.['Site Country'] || ''
  }`;
  const turbineNumber =
    damageDetails.reportJsonMetadata['TURBINE MASTER DATA']?.['Turbine/Pad Number'] || '';

  const turbineModel =
    damageDetails.reportJsonMetadata['TURBINE MASTER DATA']?.['Turbine Model'] !== 'UNKNOWN'
      ? damageDetails.reportJsonMetadata['TURBINE MASTER DATA']?.['Turbine Model']
      : '';

  const temperatureData = {
    temperature: damageDetails.reportJsonMetadata['INSPECTION DATA']?.['Ambient Temperature (C)'],
    windSpeed:
      damageDetails.reportJsonMetadata['INSPECTION DATA']?.['Wind Speed during inspection (m/s)'],
    humidity: damageDetails.reportJsonMetadata['INSPECTION DATA']?.['Ambient Humidity (%)'],
  };

  const showMenu = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (status !== selectedDamageFlagValue?.value && selectedDamageFlagValue?.value && status) {
      setSelectedDamageFlagValue(
        EDIT_APPROVAL_FLAG_OPTIONS.find((option) => option.value === status),
      );
    }
  }, [status]);

  const handleStatusChange = (event, index) => {
    event.stopPropagation();
    setSelectedDamageFlagValue(EDIT_APPROVAL_FLAG_OPTIONS[index]);
    setShowStatusFlagModal(true);
    handleMenuClose();
  };

  const onConfirmStatusModal = () => {
    setShowStatusFlagModal(false);
    patchAnnotationJob(
      {
        annotationId: damageDetails.annotationResult.data.id,
        status: selectedDamageFlagValue?.value,
      },
      {
        onSuccess: () => {
          onStatusUpdate(selectedDamageFlagValue?.value);
          setIsDamageStatusUpdated(true);
          setIsDamageUpdated(true);
        },
        onError: (error) => {
          setIsDamageUpdated(false);
          console.error(error);
        },
      },
    );
  };

  const onCancelStatusModal = () => {
    setShowStatusFlagModal(false);
    setSelectedDamageFlagValue(
      EDIT_APPROVAL_FLAG_OPTIONS.find(
        (option) => option.value === damageDetails.annotationResult.data.attributes.status,
      ),
    );
  };

  const getStatusModalFooter = () => {
    return (
      <ButtonSection>
        <CancelButton onClick={onCancelStatusModal}>{'Cancel'}</CancelButton>
        <ConfirmButton primary onClick={onConfirmStatusModal}>
          {'Confirm'}
        </ConfirmButton>
      </ButtonSection>
    );
  };
  return (
    <StyledEntityDetailHeader type={EntityType.INSPECTION_DAMAGE}>
      <div className="left">
        <div className="related">
          <div className="asset-top">
            <DetailsCurrent>
              <StyledHeaderCurrentCustomer>
                <StyledIcon icon={Icons.PERSON} />
                <Tooltip title={customerName} zIndex={elevations.P20} placement="top">
                  <CustomerName>{customerName}</CustomerName>
                </Tooltip>
                <DetailsCurrentSeparator>/</DetailsCurrentSeparator>
              </StyledHeaderCurrentCustomer>
              <StyledHeaderCurrentSite siteName={regionName} />
            </DetailsCurrent>
          </div>
        </div>
      </div>
      <div className="details">
        <TurbineIcon />
        <StyledDetailHeader>
          <StyledTurbineName>{turbineNumber}</StyledTurbineName>
          <StyledAddress>{contactAddress}</StyledAddress>
        </StyledDetailHeader>
        <AnamoliesWeatherContainer>
          <Anamolies>
            <TurbineModel>{turbineModel}</TurbineModel>
          </Anamolies>
          <DamageDetailsWeather weather={temperatureData} />
        </AnamoliesWeatherContainer>
        <Divider />

        <DropDown>
          <DropDownTitle>Status:</DropDownTitle> {selectedDamageFlagValue?.value?.toUpperCase()}{' '}
          <AuthRender
            capability={Capability.REI_RESULTS}
            edit
            description="update damage status"
            siteLevel={false}
          >
            <Button
              onClick={(e) => {
                showMenu(e);
              }}
              disabled={isEdited}
            >
              <HeaderIcon size={10} icon={Icons.PENCIL} />
            </Button>
          </AuthRender>
        </DropDown>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          width={'150'}
          padding={'0px'}
        >
          {EDIT_APPROVAL_FLAG_OPTIONS.map((flagOption, index) => {
            const isOptionSelected = selectedDamageFlagValue?.value === flagOption.value;
            return (
              <StyleApprovalFlagText
                key={index}
                onClick={(e) => !isOptionSelected && handleStatusChange(e, index)}
              >
                {flagOption.label.toUpperCase()}
                {isOptionSelected && <StyledIcon icon={Icons.CHECK} className="check-icon" />}
              </StyleApprovalFlagText>
            );
          })}
        </Menu>
      </div>
      {showStatusFlagModal && (
        <Dialog
          isOpen={true}
          onClose={onCancelStatusModal}
          header={'OPEN/CLOSE'}
          footer={getStatusModalFooter()}
        >
          <div>{'Are you sure you want to change status to:'}</div>
          <StatusFlagText>{selectedDamageFlagValue?.label}</StatusFlagText>
        </Dialog>
      )}
    </StyledEntityDetailHeader>
  );
};

DamageDetailsHeader.propTypes = {
  damageDetails: PropTypes.instanceOf(Object),
  assetSiteId: PropTypes.any,
  onStatusUpdate: PropTypes.func,
  status: PropTypes.any,
  isEdited: PropTypes.any,
};

export default DamageDetailsHeader;
